// filepath: apps/web/src/editors/bpm-designer/BpmPropertiesPanel.tsx
//
// FR-UI-BLD-1 AC-3 - right-hand properties panel for the BPM designer.
// Per-node config panel based on node type:
//   - userTask: form_definition_id (dropdown), candidate_role_id
//     (dropdown), optional assignee_user_id, optional due_in_seconds
//   - scriptTask: script_id (dropdown), optional formula
//   - timerCatch: delay_expression (formula input)
//   - xorGateway + andGateway + start/end: no config
//   - Process-level: name, description.
//   - Edge properties: guard formula (BpmEdgeGuardInput).
//
// INV-2: ALL fields render regardless of any visibility formula. The
// properties panel is an authoring surface, not a runtime renderer.

import React from "react";
import { useTranslation } from "react-i18next";
import type {
  EdgeDefinition,
  FormSummary,
  NodeDefinition,
  ProcessDraft,
  RoleSummary,
  ScriptSummary,
  ValidationErrorWire,
} from "./types";
import { BpmEdgeGuardInput } from "./BpmEdgeGuardInput";
import { InlineNodeError } from "./InlineNodeError";

interface BpmPropertiesPanelProps {
  draft: ProcessDraft;
  selectedNodeId?: string;
  selectedEdgeId?: string;
  onChangeSelectedNode: (nodeId: string, next: NodeDefinition) => void;
  onChangeSelectedEdge: (edgeId: string, next: Partial<EdgeDefinition>) => void;
  onChangeProcessMeta: (
    next: Partial<Pick<ProcessDraft, "name" | "description">>,
  ) => void;
  forms: FormSummary[];
  roles: RoleSummary[];
  scripts: ScriptSummary[];
  errors: ValidationErrorWire[];
}

export function BpmPropertiesPanel({
  draft,
  selectedNodeId,
  selectedEdgeId,
  onChangeSelectedNode,
  onChangeSelectedEdge,
  onChangeProcessMeta,
  forms,
  roles,
  scripts,
  errors,
}: BpmPropertiesPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const selectedNode = selectedNodeId
    ? draft.nodes.find((n) => n.node_id === selectedNodeId)
    : undefined;
  const selectedEdge = selectedEdgeId
    ? draft.edges.find((e) => e.edge_id === selectedEdgeId)
    : undefined;

  return (
    <aside
      className="bpm-properties-panel"
      aria-label={t("admin.designer.settings.aria")}
      data-testid="bpm-properties-panel"
    >
      <h2 className="bpm-properties-panel__title">
        {t("admin.designer.settings.title")}
      </h2>
      <div className="bpm-properties-panel__process-level">
        <label htmlFor="bpm-properties-name">
          {t("admin.designer.settings.process_name")}
        </label>
        <input
          id="bpm-properties-name"
          type="text"
          value={draft.name}
          onChange={(e) => onChangeProcessMeta({ name: e.target.value })}
          data-testid="bpm-properties-name"
        />
        <label htmlFor="bpm-properties-description">
          {t("admin.designer.settings.process_description")}
        </label>
        <textarea
          id="bpm-properties-description"
          value={draft.description ?? ""}
          onChange={(e) => onChangeProcessMeta({ description: e.target.value })}
          data-testid="bpm-properties-description"
        />
      </div>
      {selectedNode ? (
        <div className="bpm-properties-panel__node-level">
          <h3 className="bpm-properties-panel__node-title">
            {t("admin.designer.settings.node_heading", {
              id: selectedNode.node_id,
            })}
          </h3>
          <label htmlFor="bpm-properties-node-label">
            {t("admin.designer.settings.node_label")}
          </label>
          <input
            id="bpm-properties-node-label"
            type="text"
            value={selectedNode.label ?? ""}
            onChange={(e) =>
              onChangeSelectedNode(selectedNode.node_id, {
                ...selectedNode,
                label: e.target.value,
              })
            }
            data-testid="bpm-properties-node-label"
          />
          {selectedNode.node_type === "userTask" && (
            <>
              <label htmlFor="bpm-properties-form">
                {t("admin.designer.settings.form_id")}
              </label>
              <select
                id="bpm-properties-form"
                value={selectedNode.form_definition_id ?? ""}
                onChange={(e) =>
                  onChangeSelectedNode(selectedNode.node_id, {
                    ...selectedNode,
                    form_definition_id: e.target.value,
                  })
                }
                data-testid="bpm-properties-form"
              >
                <option value="">-</option>
                {forms.map((f) => (
                  <option key={f.form_id} value={f.form_id}>
                    {f.form_id}
                  </option>
                ))}
              </select>
              <label htmlFor="bpm-properties-role">
                {t("admin.designer.settings.candidate_role_id")}
              </label>
              <select
                id="bpm-properties-role"
                value={selectedNode.candidate_role_id ?? ""}
                onChange={(e) =>
                  onChangeSelectedNode(selectedNode.node_id, {
                    ...selectedNode,
                    candidate_role_id: e.target.value,
                  })
                }
                data-testid="bpm-properties-role"
              >
                <option value="">-</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <label htmlFor="bpm-properties-assignee">
                {t("admin.designer.settings.assignee_user_id")}
              </label>
              <input
                id="bpm-properties-assignee"
                type="text"
                value={selectedNode.assignee_user_id ?? ""}
                onChange={(e) =>
                  onChangeSelectedNode(selectedNode.node_id, {
                    ...selectedNode,
                    assignee_user_id: e.target.value,
                  })
                }
                data-testid="bpm-properties-assignee"
              />
              <label htmlFor="bpm-properties-due">
                {t("admin.designer.settings.due_in_seconds")}
              </label>
              <input
                id="bpm-properties-due"
                type="number"
                value={selectedNode.due_in_seconds ?? ""}
                onChange={(e) =>
                  onChangeSelectedNode(selectedNode.node_id, {
                    ...selectedNode,
                    due_in_seconds:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                data-testid="bpm-properties-due"
              />
            </>
          )}
          {selectedNode.node_type === "scriptTask" && (
            <>
              <label htmlFor="bpm-properties-script">
                {t("admin.designer.settings.script_id")}
              </label>
              <select
                id="bpm-properties-script"
                value={selectedNode.script_id ?? ""}
                onChange={(e) =>
                  onChangeSelectedNode(selectedNode.node_id, {
                    ...selectedNode,
                    script_id: e.target.value,
                  })
                }
                data-testid="bpm-properties-script"
              >
                <option value="">-</option>
                {scripts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <label htmlFor="bpm-properties-formula">
                {t("admin.designer.settings.script_formula")}
              </label>
              <input
                id="bpm-properties-formula"
                type="text"
                value={selectedNode.formula ?? ""}
                onChange={(e) =>
                  onChangeSelectedNode(selectedNode.node_id, {
                    ...selectedNode,
                    formula: e.target.value,
                  })
                }
                data-testid="bpm-properties-formula"
              />
            </>
          )}
          {selectedNode.node_type === "timerCatch" && (
            <>
              <label htmlFor="bpm-properties-delay">
                {t("admin.designer.settings.delay_expression")}
              </label>
              <input
                id="bpm-properties-delay"
                type="text"
                value={selectedNode.delay_expression ?? ""}
                onChange={(e) =>
                  onChangeSelectedNode(selectedNode.node_id, {
                    ...selectedNode,
                    delay_expression: e.target.value,
                  })
                }
                data-testid="bpm-properties-delay"
              />
            </>
          )}
          {(selectedNode.node_type === "start" ||
            selectedNode.node_type === "end" ||
            selectedNode.node_type === "xorGateway" ||
            selectedNode.node_type === "andGateway") && (
            <p
              className="bpm-properties-panel__no-config"
              data-testid="bpm-properties-no-config"
            >
              {t("admin.designer.settings.no_config")}
            </p>
          )}
          <InlineNodeError nodeId={selectedNode.node_id} errors={errors} />
        </div>
      ) : selectedEdge ? (
        <div className="bpm-properties-panel__edge-level">
          <h3 className="bpm-properties-panel__edge-title">
            {t("admin.designer.settings.edge_heading", {
              id: selectedEdge.edge_id,
            })}
          </h3>
          <label htmlFor="bpm-properties-guard">
            {t("admin.designer.settings.edge_guard")}
          </label>
          <BpmEdgeGuardInput
            edgeId={selectedEdge.edge_id}
            value={selectedEdge.guard_formula ?? ""}
            onChange={(next) =>
              onChangeSelectedEdge(selectedEdge.edge_id, {
                guard_formula: next,
              })
            }
            ariaLabel={t("admin.designer.settings.edge_guard")}
          />
        </div>
      ) : (
        <p
          className="bpm-properties-panel__no-selection"
          data-testid="bpm-properties-no-selection"
        >
          {t("admin.designer.settings.no_selection")}
        </p>
      )}
    </aside>
  );
}
